import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { ChartPeriod } from '../../../../jobsites-management/jobsite-summary/jobsite-productivity/models/productivity/productivity.model'
import { ProgressCategory } from 'app/jobsites-management/jobsite-summary/models/progress/progress-category.enum'
import { TranslateService } from '@ngx-translate/core'
import { UserPreferencesDto } from 'app/shared/remote-services/dtos/userPreferences.dto'
import { Permissions } from '../../../../keycloak/models/permissions.model'

@Component({
  selector: 'jobsite-follow-graph-header',
  template: `
    <mat-radio-group
      color="primary"
      [ngModel]="progressCategory"
      (change)="updateProgressCategoryEvent.emit($event.value)"
    >
      @for (category of categoriesToChooseFrom; track $index) {
        <mat-radio-button [value]="category">
          {{ getCategoryHeaderTranslation(category, userPreferences) }}
        </mat-radio-button>
      }
    </mat-radio-group>
    <div class="header-center">
      @if (displayProgressButton) {
        <button
          mat-icon-button
          class="planned-button"
          disableRipple
          [disabled]="!enableProgressBtn"
          (click)="toggleEditPlannedProgress()"
          matTooltip="{{
            'JOBSITE_MANAGEMENT.FOLLOW_UP.EDIT_PLANNED_PROGRESS' | translate
          }}"
        >
          <mat-icon>addchart</mat-icon>
        </button>
      }

      <span class="chart-title">
        {{ chartTitle }}
      </span>

      @if (showPeriodicityMenu) {
        <button
          mat-icon-button
          disableRipple
          [matMenuTriggerFor]="periodicityMenu"
        >
          <mat-icon> calendar_today</mat-icon>
        </button>
      }
      @if (enableExport) {
        <button
          mat-icon-button
          disableRipple
          matTooltip="{{ 'MAP_BUTTONS.EXPORT_CSV' | translate }}"
          (click)="exportCSV()"
        >
          <mat-icon fontSet="fa" fontIcon="fa-download"></mat-icon>
        </button>
      }
      @if (enableAdminExport) {
        <button
          mat-icon-button
          disableRipple
          matTooltip="{{ 'MAP_BUTTONS.ADMIN_EXPORT_CSV' | translate }}"
          (click)="exportAdminCSV()"
        >
          <mat-icon fontSet="fa" fontIcon="fa-download"></mat-icon>
        </button>
      }
      <mat-menu #periodicityMenu>
        <button mat-menu-item (click)="updateProgressPeriodEvent.emit('DAILY')">
          {{ 'PROJECT_MANAGEMENT.PRODUCTIVITY.DAILY' | translate }}
        </button>
        <button
          mat-menu-item
          (click)="updateProgressPeriodEvent.emit('WEEKLY')"
        >
          {{ 'PROJECT_MANAGEMENT.PRODUCTIVITY.WEEKLY' | translate }}
        </button>
        <button
          mat-menu-item
          (click)="updateProgressPeriodEvent.emit('MONTHLY')"
        >
          {{ 'PROJECT_MANAGEMENT.PRODUCTIVITY.MONTHLY' | translate }}
        </button>
      </mat-menu>
    </div>
  `,
  styles: [
    `
      :host {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: 1fr;
        align-content: center;
      }

      .header-center {
        display: flex;
        justify-content: center;
      }

      .mat-radio-group {
        display: flex;
        align-items: center;
        gap: 5%;
      }

      .mat-radio-button {
        display: flex;
      }

      .planned-button {
        z-index: 20;
      }

      .chart-title {
        margin: auto 25px;
        font-size: 20px;
        white-space: nowrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class JobsiteFollowGraphHeaderComponent implements OnInit {
  private translateService = inject(TranslateService)

  @Input({ required: true }) chartTitle: string
  @Input({ required: true }) enableProgressBtn: boolean
  @Input({ required: true }) userPreferences: UserPreferencesDto
  @Input({ required: true }) category: ProgressCategory
  @Input({ required: true }) userPermission: Permissions
  @Input() categoriesToChooseFrom: ProgressCategory[] = []
  @Input() enableExport = false
  @Input() enableAdminExport = false
  @Input() showPeriodicityMenu = true
  @Input() showProgressButton = true

  progressCategory: ProgressCategory

  @Output() updateProgressPeriodEvent = new EventEmitter<ChartPeriod>()
  @Output() updateProgressCategoryEvent = new EventEmitter<ProgressCategory>()
  @Output() openEditPlanedDialogEvent = new EventEmitter<null>()
  @Output() exportProductivityChartEvent = new EventEmitter<null>()
  @Output() adminExportProductivityChartEvent = new EventEmitter<null>()

  ngOnInit(): void {
    this.progressCategory = this.category ?? ProgressCategory.COUNT
  }

  getCategoryHeaderTranslation(
    category: ProgressCategory,
    userPreferences: UserPreferencesDto,
  ) {
    switch (category) {
      case ProgressCategory.AREA_SIMPLE:
        return this.translateService.instant(
          'JOBSITE_MANAGEMENT.FOLLOW_UP.CHOICE_' + category,
          {
            unit: userPreferences.unitSystem === 'imperial' ? 'in²' : 'm²',
          },
        )
      case ProgressCategory.LENGTH:
        return this.translateService.instant(
          `JOBSITE_MANAGEMENT.FOLLOW_UP.CHOICE_${category}_${userPreferences.unitSystem.toUpperCase()}`,
        )
      default:
        return this.translateService.instant(
          'JOBSITE_MANAGEMENT.FOLLOW_UP.CHOICE_' + category,
        )
    }
  }

  toggleEditPlannedProgress(): void {
    if (this.enableProgressBtn) {
      this.openEditPlanedDialogEvent.emit()
    }
  }

  exportCSV(): void {
    if (this.enableExport) {
      this.exportProductivityChartEvent.emit()
    }
  }
  exportAdminCSV(): void {
    if (this.enableAdminExport) {
      this.adminExportProductivityChartEvent.emit()
    }
  }

  get displayProgressButton(): boolean {
    return (
      this.showProgressButton &&
      this.userPermission.grants.updatePlannedProgress
    )
  }
}
